import { useCallback, useState, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import { chatBotService } from '../../services/ChatBotService';
import { type EndChatFlowState, type SurveyState } from '../../types';

interface UseEndChatFlowParams {
  isSessionReadyRef: MutableRefObject<boolean>;
  resetSession: (options?: { closeWidget?: boolean; resetSurvey?: boolean }) => void;
  surveyState: SurveyState;
  setSurveyState: Dispatch<SetStateAction<SurveyState>>;
}

interface UseEndChatFlowResult {
  endChatFlowState: EndChatFlowState;
  initiateEndChat: () => void;
  cancelEndChat: () => void;
  closeThankYou: () => void;
  confirmEndChat: () => Promise<void>;
  openInlineFeedbackSurvey: () => Promise<void>;
  submitSurvey: (scores: Record<number, number>, comments: string) => Promise<string | null>;
  skipSurvey: () => Promise<void>;
}

export const useEndChatFlow = ({
  isSessionReadyRef,
  resetSession,
  surveyState,
  setSurveyState,
}: UseEndChatFlowParams): UseEndChatFlowResult => {
  const [endChatFlowState, setEndChatFlowState] = useState<EndChatFlowState>('idle');
  const [surveyEntryPoint, setSurveyEntryPoint] = useState<'end-chat' | 'inline'>('end-chat');

  const initiateEndChat = useCallback(() => {
    if (!isSessionReadyRef.current) {
      resetSession({ closeWidget: true });
      return;
    }

    setEndChatFlowState('confirming');
  }, [isSessionReadyRef, resetSession]);

  const cancelEndChat = useCallback(() => {
    setEndChatFlowState('idle');
  }, []);

  const closeThankYou = useCallback(() => {
    resetSession({ closeWidget: true, resetSurvey: true });
    setEndChatFlowState('idle');
  }, [resetSession]);

  const confirmEndChat = useCallback(async () => {
    setSurveyEntryPoint('end-chat');
    setEndChatFlowState('loading-survey');

    const feedbackResponse = await chatBotService.getFeedbackQuestions();

    if (feedbackResponse && feedbackResponse.questions.length > 0) {
      setSurveyState({
        feedbackSurveyID: feedbackResponse.feedbackSurveyID,
        questions: feedbackResponse.questions,
        scores: {},
        comments: '',
      });
      setEndChatFlowState('survey');
      return;
    }

    resetSession({ closeWidget: true });
    setEndChatFlowState('idle');
  }, [resetSession, setSurveyState]);

  const openInlineFeedbackSurvey = useCallback(async () => {
    setSurveyEntryPoint('inline');
    setEndChatFlowState('loading-survey');

    const feedbackResponse = await chatBotService.getFeedbackQuestions();

    if (feedbackResponse && feedbackResponse.questions.length > 0) {
      setSurveyState({
        feedbackSurveyID: feedbackResponse.feedbackSurveyID,
        questions: feedbackResponse.questions,
        scores: {},
        comments: '',
      });
      setEndChatFlowState('survey');
      return;
    }

    setEndChatFlowState('idle');
  }, [setSurveyState]);

  const submitSurvey = useCallback(async (scores: Record<number, number>, comments: string): Promise<string | null> => {
    if (!surveyState.feedbackSurveyID) {
      return null;
    }

    setEndChatFlowState('submitting');

    const scoresPayload = Object.entries(scores).map(([qId, score]) => ({
      questionID: Number(qId),
      score,
    }));

    try {
      const submitResult = await chatBotService.submitFeedback({
        feedbackSurveyID: surveyState.feedbackSurveyID,
        isSkipped: false,
        scores: scoresPayload,
        comments: comments || undefined,
      });

      if (submitResult.success) {
        if (surveyEntryPoint === 'inline') {
          setSurveyState({ feedbackSurveyID: null, questions: [], scores: {}, comments: '' });
          setEndChatFlowState('idle');
          return null;
        }

        setEndChatFlowState('thank-you');
        resetSession({ resetSurvey: true });
        return null;
      }

      setEndChatFlowState('survey');
      return submitResult.errorMessage || null;
    } catch {
      setEndChatFlowState('survey');
      return null;
    }
  }, [resetSession, setSurveyState, surveyEntryPoint, surveyState.feedbackSurveyID]);

  const skipSurvey = useCallback(async () => {
    setEndChatFlowState('submitting');

    try {
      if (!surveyState.feedbackSurveyID) {
        setEndChatFlowState('survey');
        return;
      }

      const didSubmit = await chatBotService.submitFeedback({
        feedbackSurveyID: surveyState.feedbackSurveyID,
        isSkipped: true,
      });

      if (didSubmit.success) {
        if (surveyEntryPoint === 'inline') {
          setSurveyState({ feedbackSurveyID: null, questions: [], scores: {}, comments: '' });
          setEndChatFlowState('idle');
          return;
        }

        
        resetSession({ closeWidget: true, resetSurvey: true });
        setEndChatFlowState('idle');
        return;
      }

      setEndChatFlowState('survey');
    } catch {
      setEndChatFlowState('survey');
    }
  }, [resetSession, setSurveyState, surveyEntryPoint, surveyState.feedbackSurveyID]);

  return {
    endChatFlowState,
    initiateEndChat,
    cancelEndChat,
    closeThankYou,
    confirmEndChat,
    openInlineFeedbackSurvey,
    submitSurvey,
    skipSurvey,
  };
};
